import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, ProgressBar } from "react-bootstrap";
import { getEdificiosOperativos } from "../../utils";
import { getLocale } from "../../utils/getLocale";

/**
 * Modal para asignar edificios a un mes específico de un contrato AR.
 *
 * Props:
 *  - show: boolean
 *  - onHide: () => void
 *  - onSave: (selectedIds, edificiosTotales, pantallasTotales, personasAsignadas) => void
 *  - pais: string
 *  - personasComprometidas: number (audiencia objetivo del contrato)
 *  - edificiosIdsActuales: string | null (IDs pre-seleccionados, comma-separated)
 *  - mesLabel: string (label para mostrar, ej "MAR/26")
 */
export const ModalAsignarEdificios = ({
  show,
  onHide,
  onSave,
  pais,
  personasComprometidas,
  edificiosIdsActuales,
  mesLabel,
}) => {
  const [edificios, setEdificios] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Cargar edificios operativos
  useEffect(() => {
    if (!show) return;
    const fetch = async () => {
      const data = await getEdificiosOperativos({ pais });
      setEdificios((data || []).sort((a, b) => a.edificio.localeCompare(b.edificio)));
    };
    fetch();
  }, [show, pais]);

  // Pre-seleccionar edificios existentes
  useEffect(() => {
    if (!show) return;
    if (edificiosIdsActuales && edificiosIdsActuales.trim() !== "") {
      const ids = new Set(edificiosIdsActuales.split(",").map((s) => Number(s.trim())));
      setSelectedIds(ids);
    } else {
      setSelectedIds(new Set());
    }
  }, [show, edificiosIdsActuales]);

  const toggleEdificio = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const edificiosFiltrados = useMemo(() => {
    if (!search.trim()) return edificios;
    const q = search.toLowerCase();
    return edificios.filter(
      (e) => e.edificio.toLowerCase().includes(q) || (e.ubicacion && e.ubicacion.toLowerCase().includes(q))
    );
  }, [edificios, search]);

  const totales = useMemo(() => {
    let personas = 0;
    let pantallas = 0;
    let count = 0;
    edificios.forEach((e) => {
      if (selectedIds.has(e.id)) {
        personas += Number(e.personas) || 0;
        pantallas +=
          (Number(e.pantallas) || 0) + (Number(e.espera) || 0) + (Number(e.totems) || 0) + (Number(e.cowork) || 0);
        count++;
      }
    });
    return { personas, pantallas, count };
  }, [edificios, selectedIds]);

  const porcentajeAudiencia = personasComprometidas > 0 ? (totales.personas / personasComprometidas) * 100 : 0;

  const handleSave = () => {
    setLoading(true);
    const idsString = [...selectedIds].join(",");

    // Clasificar edificios por tipo de pantalla para comisión edificios
    const ascensoresIds = [];
    const esperaIds = [];
    const circulacionIds = [];
    const coworkIds = [];
    edificios.forEach((e) => {
      if (!selectedIds.has(e.id)) return;
      if ((Number(e.pantallas) || 0) > 0) ascensoresIds.push(e.id);
      if ((Number(e.espera) || 0) > 0) esperaIds.push(e.id);
      if ((Number(e.totems) || 0) > 0) circulacionIds.push(e.id);
      if ((Number(e.cowork) || 0) > 0) coworkIds.push(e.id);
    });

    onSave(idsString, totales.count, totales.pantallas, totales.personas, {
      pantallas_ascensores: ascensoresIds.join(","),
      pantallas_espera: esperaIds.join(","),
      pantallas_circulacion: circulacionIds.join(","),
      pantallas_cowork: coworkIds.join(","),
    });
    setLoading(false);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Asignar Edificios — {mesLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Progreso audiencia */}
        {personasComprometidas > 0 && (
          <div className="mb-3 p-2 border rounded bg-light">
            <div className="d-flex justify-content-between mb-1">
              <small>
                <strong>Audiencia contratada:</strong> {personasComprometidas.toLocaleString(getLocale(pais))}
              </small>
              <small>
                <strong>Asignada:</strong> {totales.personas.toLocaleString(getLocale(pais))} (
                {porcentajeAudiencia.toFixed(1)}%)
              </small>
            </div>
            <ProgressBar
              now={Math.min(porcentajeAudiencia, 100)}
              variant={porcentajeAudiencia >= 100 ? "success" : porcentajeAudiencia >= 75 ? "warning" : "danger"}
              label={`${Math.min(porcentajeAudiencia, 100).toFixed(0)}%`}
            />
          </div>
        )}

        {/* Resumen selección */}
        <div className="mb-2 d-flex gap-3">
          <small>
            <strong>Edificios:</strong> {totales.count}
          </small>
          <small>
            <strong>Pantallas:</strong> {totales.pantallas}
          </small>
          <small>
            <strong>Personas:</strong> {totales.personas.toLocaleString(getLocale(pais))}
          </small>
        </div>

        {/* Buscador */}
        <Form.Control
          type="text"
          placeholder="Buscar edificio..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2"
          size="sm"
        />

        {/* Lista de edificios */}
        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          <table className="table table-sm table-hover mb-0">
            <colgroup>
              <col style={{ width: "60px" }} />
              <col />
              <col />
              <col style={{ width: "100px" }} />
              <col style={{ width: "110px" }} />
            </colgroup>
            <thead className="sticky-top">
              <tr>
                <th style={{ backgroundColor: "#212529", color: "#fff" }} className="text-center" />
                <th style={{ backgroundColor: "#212529", color: "#fff" }} className="text-start">
                  Edificio
                </th>
                <th style={{ backgroundColor: "#212529", color: "#fff" }} className="text-start">
                  Ubicación
                </th>
                <th style={{ backgroundColor: "#212529", color: "#fff" }} className="text-center">
                  Pantallas
                </th>
                <th style={{ backgroundColor: "#212529", color: "#fff" }} className="text-center">
                  Personas
                </th>
              </tr>
            </thead>
            <tbody>
              {edificiosFiltrados.map((e) => {
                const totalPantallas =
                  (Number(e.pantallas) || 0) +
                  (Number(e.espera) || 0) +
                  (Number(e.totems) || 0) +
                  (Number(e.cowork) || 0);
                return (
                  <tr
                    key={e.id}
                    onClick={() => toggleEdificio(e.id)}
                    style={{ cursor: "pointer" }}
                    className={selectedIds.has(e.id) ? "table-success" : ""}
                  >
                    <td className="text-center">
                      <Form.Check type="checkbox" checked={selectedIds.has(e.id)} onChange={() => {}} />
                    </td>
                    <td className="text-start">{e.edificio}</td>
                    <td className="text-start text-muted small">{e.ubicacion || "-"}</td>
                    <td className="text-center">{totalPantallas}</td>
                    <td className="text-center">{(e.personas || 0).toLocaleString(getLocale(pais))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} size="sm">
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading} size="sm">
          {loading ? "Guardando..." : "Guardar Edificios"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
